import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  ArrowLeftIcon,
  BuildingOfficeIcon,
  UserIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';
import agencyService from '../../services/agencyService';
import userService from '../../services/userService';
import { useToast } from '../../contexts/ToastContext';

const AgencyForm = () => {
  const { id } = useParams();
  const isEditMode = !!id;
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  
  const [loading, setLoading] = useState(false);
  const [managers, setManagers] = useState([]);
  const [agencyData, setAgencyData] = useState(null);

  const validationSchema = Yup.object({
    name: Yup.string()
      .required('Le nom est requis')
      .min(3, 'Le nom doit contenir au moins 3 caractères'),
    code: Yup.string()
      .required('Le code est requis')
      .matches(/^[A-Z0-9]+$/, 'Le code doit contenir uniquement des lettres majuscules et chiffres'),
    address: Yup.string(),
    phone: Yup.string()
      .matches(/^[0-9\s\-+()]*$/, 'Numéro de téléphone invalide'),
    email: Yup.string()
      .email('Email invalide'),
    manager_id: Yup.number()
      .nullable(),
  });

  const formik = useFormik({
    initialValues: {
      name: '',
      code: '',
      address: '',
      phone: '',
      email: '',
      manager_id: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        
        const formattedValues = {
          ...values,
          manager_id: values.manager_id ? parseInt(values.manager_id) : null,
        };

        if (isEditMode) {
          await agencyService.updateAgency(id, formattedValues);
          showSuccess('Agence mise à jour avec succès');
        } else {
          await agencyService.createAgency(formattedValues);
          showSuccess('Agence créée avec succès');
        }
        
        navigate('/agencies');
      } catch (error) {
        showError(error.response?.data?.message || 'Une erreur est survenue');
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    fetchManagers();
    if (isEditMode) {
      fetchAgency();
    }
  }, [id]);

  const fetchManagers = async () => {
    try {
      const users = await userService.getAllUsers();
      const managersList = users.filter(user => 
        user.role_name?.toLowerCase().includes('manager') || 
        user.role_name?.toLowerCase().includes('admin')
      );
      setManagers(managersList);
    } catch (error) {
      console.error('Error fetching managers:', error);
    }
  };

  const fetchAgency = async () => {
    try {
      setLoading(true);
      const data = await agencyService.getAgency(id);
      setAgencyData(data);
      
      // Set form values
      Object.keys(data).forEach(key => {
        if (formik.values[key] !== undefined) {
          formik.setFieldValue(key, data[key]);
        }
      });
    } catch (error) {
      showError('Erreur lors du chargement de l\'agence');
      navigate('/agencies');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/agencies');
  };

  if (loading && isEditMode && !agencyData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <svg className="animate-spin h-8 w-8 text-primary-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-2 text-sm text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <button
            onClick={() => navigate('/agencies')}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Retour aux agences
          </button>
          <h1 className="text-2xl font-semibold text-gray-900">
            {isEditMode ? 'Modifier l\'Agence' : 'Nouvelle Agence'}
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            {isEditMode
              ? 'Mettez à jour les informations de l\'agence'
              : 'Créez une nouvelle agence pour votre réseau'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form Section */}
        <div className="lg:col-span-2">
          <div className="card">
            <div className="p-6">
              <form onSubmit={formik.handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  {/* Name */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                      Nom de l'agence *
                    </label>
                    <div className="mt-1">
                      <div className="flex items-center">
                        <BuildingOfficeIcon className="h-5 w-5 text-gray-400 mr-3" />
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formik.values.name}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          className="input-field flex-1"
                          placeholder="Ex: Agence Centrale"
                        />
                      </div>
                      {formik.touched.name && formik.errors.name && (
                        <p className="mt-2 text-sm text-red-600">{formik.errors.name}</p>
                      )}
                    </div>
                  </div>

                  {/* Code */}
                  <div>
                    <label htmlFor="code" className="block text-sm font-medium text-gray-700">
                      Code unique *
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        id="code"
                        name="code"
                        value={formik.values.code}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className="input-field uppercase"
                        placeholder="Ex: AG001"
                        disabled={isEditMode}
                      />
                      {formik.touched.code && formik.errors.code && (
                        <p className="mt-2 text-sm text-red-600">{formik.errors.code}</p>
                      )}
                    </div>
                  </div>

                  {/* Address */}
                  <div className="sm:col-span-2">
                    <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                      Adresse
                    </label>
                    <div className="mt-1">
                      <div className="flex items-start">
                        <MapPinIcon className="h-5 w-5 text-gray-400 mr-3 mt-3" />
                        <textarea
                          id="address"
                          name="address"
                          rows={3}
                          value={formik.values.address}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          className="input-field flex-1"
                          placeholder="Adresse complète de l'agence"
                        />
                      </div>
                      {formik.touched.address && formik.errors.address && (
                        <p className="mt-2 text-sm text-red-600">{formik.errors.address}</p>
                      )}
                    </div>
                  </div>

                  {/* Phone */}
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                      Téléphone
                    </label>
                    <div className="mt-1">
                      <div className="flex items-center">
                        <PhoneIcon className="h-5 w-5 text-gray-400 mr-3" />
                        <input
                          type="text"
                          id="phone"
                          name="phone"
                          value={formik.values.phone}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          className="input-field flex-1"
                          placeholder="Ex: +221 33 123 45 67"
                        />
                      </div>
                      {formik.touched.phone && formik.errors.phone && (
                        <p className="mt-2 text-sm text-red-600">{formik.errors.phone}</p>
                      )}
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                      Email
                    </label>
                    <div className="mt-1">
                      <div className="flex items-center">
                        <EnvelopeIcon className="h-5 w-5 text-gray-400 mr-3" />
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formik.values.email}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          className="input-field flex-1"
                          placeholder="agence@exemple.com"
                        />
                      </div>
                      {formik.touched.email && formik.errors.email && (
                        <p className="mt-2 text-sm text-red-600">{formik.errors.email}</p>
                      )}
                    </div>
                  </div>

                  {/* Manager */}
                  <div className="sm:col-span-2">
                    <label htmlFor="manager_id" className="block text-sm font-medium text-gray-700">
                      Responsable
                    </label>
                    <div className="mt-1">
                      <div className="flex items-center">
                        <UserIcon className="h-5 w-5 text-gray-400 mr-3" />
                        <select
                          id="manager_id"
                          name="manager_id"
                          value={formik.values.manager_id}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          className="input-field flex-1"
                        >
                          <option value="">Sélectionnez un responsable</option>
                          {managers.map(manager => (
                            <option key={manager.id} value={manager.id}>
                              {manager.first_name} {manager.last_name} ({manager.role_name})
                            </option>
                          ))}
                        </select>
                      </div>
                      {formik.touched.manager_id && formik.errors.manager_id && (
                        <p className="mt-2 text-sm text-red-600">{formik.errors.manager_id}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="btn-secondary"
                    disabled={loading}
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={loading || !formik.isValid}
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {isEditMode ? 'Mise à jour...' : 'Création...'}
                      </>
                    ) : (
                      isEditMode ? 'Mettre à jour' : 'Créer l\'agence'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="space-y-6">
            {/* Information */}
            <div className="card">
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Informations</h3>
                <ul className="space-y-3">
                  <li className="flex justify-between">
                    <span className="text-sm text-gray-600">Date de création</span>
                    <span className="text-sm font-medium text-gray-900">
                      {agencyData?.created_at 
                        ? new Date(agencyData.created_at).toLocaleDateString('fr-FR')
                        : new Date().toLocaleDateString('fr-FR')}
                    </span>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-sm text-gray-600">Statut</span>
                    <span className="badge badge-success">
                      Active
                    </span>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-sm text-gray-600">Clients</span>
                    <span className="text-sm font-medium text-gray-900">
                      {agencyData?.total_clients || 0}
                    </span>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-sm text-gray-600">Prêts actifs</span>
                    <span className="text-sm font-medium text-gray-900">
                      {agencyData?.active_loans || 0}
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Tips */}
            <div className="card">
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Conseils</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <div className="flex-shrink-0 h-5 w-5 text-green-500 mt-0.5">✓</div>
                    <p className="ml-3 text-sm text-gray-600">
                      Le code doit être unique et ne peut être modifié après création
                    </p>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 h-5 w-5 text-green-500 mt-0.5">✓</div>
                    <p className="ml-3 text-sm text-gray-600">
                      Assignez un responsable pour une meilleure gestion
                    </p>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 h-5 w-5 text-green-500 mt-0.5">✓</div>
                    <p className="ml-3 text-sm text-gray-600">
                      Les informations de contact doivent être à jour
                    </p>
                  </li>
                </ul>
              </div>
            </div>

            {/* Quick Actions */}
            {isEditMode && (
              <div className="card">
                <div className="p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Actions rapides</h3>
                  <div className="space-y-3">
                    <button
                      onClick={() => navigate(`/agencies/${id}/statistics`)}
                      className="w-full btn-secondary"
                    >
                      Voir les statistiques
                    </button>
                    <button
                      onClick={() => navigate(`/agencies/${id}/users`)}
                      className="w-full btn-secondary"
                    >
                      Gérer les utilisateurs
                    </button>
                    <button
                      onClick={() => navigate(`/clients?agency=${id}`)}
                      className="w-full btn-secondary"
                    >
                      Voir les clients
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgencyForm;